/**
 * PasswordStrengthIndicator Component (DC-898)
 *
 * Displays password strength and requirement checklist.
 * Provides real-time feedback as user types.
 */

import React, { useMemo } from 'react';

interface PasswordStrengthIndicatorProps {
  password: string;
  showRequirements?: boolean;
}

type StrengthLevel = 'weak' | 'fair' | 'good' | 'strong';

interface PasswordAnalysis {
  strength: StrengthLevel;
  score: number;
  requirements: {
    minLength: boolean;
    hasUppercase: boolean;
    hasLowercase: boolean;
    hasNumber: boolean;
    hasSpecial: boolean;
  };
}

function analyzePassword(password: string): PasswordAnalysis {
  const requirements = {
    minLength: password.length >= 12,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };

  // Calculate score
  let score = 0;

  // Length scoring
  if (password.length >= 12) score += 1;
  if (password.length >= 16) score += 1;
  if (password.length >= 20) score += 1;

  // Character variety scoring
  if (requirements.hasLowercase) score += 1;
  if (requirements.hasUppercase) score += 1;
  if (requirements.hasNumber) score += 1;
  if (requirements.hasSpecial) score += 1;

  // Bonus for long passwords with good variety
  if (password.length >= 16 && score >= 6) score += 1;

  // Determine strength level
  let strength: StrengthLevel;
  if (score <= 3) {
    strength = 'weak';
  } else if (score <= 5) {
    strength = 'fair';
  } else if (score <= 7) {
    strength = 'good';
  } else {
    strength = 'strong';
  }

  return { strength, score, requirements };
}

export function PasswordStrengthIndicator({
  password,
  showRequirements = true,
}: PasswordStrengthIndicatorProps) {
  const analysis = useMemo(() => analyzePassword(password), [password]);

  const strengthLabels: Record<StrengthLevel, string> = {
    weak: 'Weak',
    fair: 'Fair',
    good: 'Good',
    strong: 'Strong',
  };

  if (!password) {
    return null;
  }

  return (
    <div className="password-strength">
      {/* Strength bar */}
      <div className="password-strength-bar">
        <div className={`password-strength-fill ${analysis.strength}`} />
      </div>

      {/* Strength label */}
      <div className={`password-strength-label ${analysis.strength}`}>
        Password Strength: {strengthLabels[analysis.strength]}
      </div>

      {/* Requirements checklist */}
      {showRequirements && (
        <div className="password-requirements">
          <PasswordRequirement
            met={analysis.requirements.minLength}
            label="At least 12 characters"
          />
          <PasswordRequirement
            met={analysis.requirements.hasUppercase}
            label="Uppercase letter"
          />
          <PasswordRequirement
            met={analysis.requirements.hasLowercase}
            label="Lowercase letter"
          />
          <PasswordRequirement
            met={analysis.requirements.hasNumber}
            label="Number"
          />
          <PasswordRequirement
            met={analysis.requirements.hasSpecial}
            label="Special character (!@#$%^&*)"
          />
        </div>
      )}
    </div>
  );
}

interface PasswordRequirementProps {
  met: boolean;
  label: string;
}

function PasswordRequirement({ met, label }: PasswordRequirementProps) {
  return (
    <div className={`password-requirement ${met ? 'met' : ''}`}>
      <span className="password-requirement-icon">{met ? '✓' : '○'}</span>
      {label}
    </div>
  );
}

/**
 * Check if password meets all requirements
 */
export function isPasswordValid(password: string): boolean {
  const analysis = analyzePassword(password);
  return (
    analysis.requirements.minLength &&
    analysis.requirements.hasUppercase &&
    analysis.requirements.hasLowercase &&
    analysis.requirements.hasNumber &&
    analysis.requirements.hasSpecial
  );
}

export default PasswordStrengthIndicator;
